import {
  useState,
  useCallback,
  useEffect,
  useRef,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

interface AppUpdateContextValue {
  needRefresh: boolean;
  isUpdating: boolean;
  isChecking: boolean;
  applyUpdate: () => void;
  dismissUpdate: () => void;
  checkForUpdate: () => Promise<UpdateCheckResult>;
}

const AppUpdateContext = createContext<AppUpdateContextValue | null>(null);

export type UpdateCheckResult =
  | "update-available"
  | "up-to-date"
  | "checking";

interface AppUpdateProviderProps {
  children: ReactNode;
}

/**
 * AppUpdateProvider - Registers the service worker and exposes update state.
 * Must wrap any components that use useAppUpdate().
 * Ensures useRegisterSW() is only called once, avoiding duplicate registrations.
 */
export function AppUpdateProvider({ children }: AppUpdateProviderProps) {
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const pollingIntervalRef = useRef<number | null>(null);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);
  const onlineHandlerRef = useRef<(() => void) | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      if (!registration) return;
      registrationRef.current = registration;

      // Safari/WebKit sometimes skips the `waiting` state or doesn't fire
      // `updatefound` on programmatic registration.update() calls. This
      // helper checks for a waiting worker after every update() call and
      // manually triggers the refresh prompt when the library misses it.
      let checkInProgress = false;
      const checkForWaiting = async () => {
        if (checkInProgress) return;
        checkInProgress = true;
        try {
          await registration.update();
          await new Promise((r) => setTimeout(r, 1500));
          if (registration.waiting) {
            setNeedRefresh(true);
          }
        } catch (e) {
          console.error("SW update check failed:", e);
        } finally {
          checkInProgress = false;
        }
      };

      // Immediate check on load
      void checkForWaiting();

      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (visibilityHandlerRef.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityHandlerRef.current
        );
        visibilityHandlerRef.current = null;
      }
      if (onlineHandlerRef.current) {
        window.removeEventListener("online", onlineHandlerRef.current);
        onlineHandlerRef.current = null;
      }

      pollingIntervalRef.current = window.setInterval(
        () => void checkForWaiting(),
        60 * 1000
      );

      const visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          void checkForWaiting();
        }
      };
      visibilityHandlerRef.current = visibilityHandler;
      document.addEventListener("visibilitychange", visibilityHandler);

      const onlineHandler = () => {
        void checkForWaiting();
      };
      onlineHandlerRef.current = onlineHandler;
      window.addEventListener("online", onlineHandler);
    },
    onRegisterError(error) {
      console.error("Service worker registration error:", error);
    },
  });

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (visibilityHandlerRef.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityHandlerRef.current
        );
        visibilityHandlerRef.current = null;
      }
      if (onlineHandlerRef.current) {
        window.removeEventListener("online", onlineHandlerRef.current);
        onlineHandlerRef.current = null;
      }
      registrationRef.current = null;
    };
  }, []);

  const dismissUpdate = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  const applyUpdate = useCallback(() => {
    setIsUpdating(true);

    let reloaded = false;
    const reload = () => {
      if (reloaded) return;
      reloaded = true;
      clearTimeout(fallbackTimer);
      navigator.serviceWorker?.removeEventListener("controllerchange", reload);
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener("controllerchange", reload);
    updateServiceWorker(true);

    // Fallback: force reload after 2s if controllerchange never fires
    const fallbackTimer = setTimeout(reload, 2000);
  }, [updateServiceWorker]);

  const checkForUpdate = useCallback(async (): Promise<UpdateCheckResult> => {
    const registration = registrationRef.current;
    if (!registration) {
      return "up-to-date";
    }

    setIsChecking(true);
    try {
      await registration.update();
      await new Promise((resolve) => setTimeout(resolve, 1500));

      if (registration.waiting || needRefresh) {
        setNeedRefresh(true);
        return "update-available";
      }

      return "up-to-date";
    } catch (error) {
      console.error("Update check failed:", error);
      return "up-to-date";
    } finally {
      setIsChecking(false);
    }
  }, [needRefresh, setNeedRefresh]);

  const value: AppUpdateContextValue = {
    needRefresh,
    isUpdating,
    isChecking,
    applyUpdate,
    dismissUpdate,
    checkForUpdate,
  };

  return (
    <AppUpdateContext.Provider value={value}>
      {children}
    </AppUpdateContext.Provider>
  );
}

/**
 * useAppUpdate - Hook to access app update state and actions.
 * Must be used within an AppUpdateProvider.
 */
export function useAppUpdate(): AppUpdateContextValue {
  const context = useContext(AppUpdateContext);
  if (!context) {
    throw new Error("useAppUpdate must be used within an AppUpdateProvider");
  }
  return context;
}
